import { useEffect, useState } from 'react'
import ReactCountryFlag from 'react-country-flag'
import reactStringReplace from 'react-string-replace'
import styled from 'styled-components'
import { useSelector } from 'react-redux'

import { translate } from '../../utils/translation'
import { fonts, mediaQueries, palette } from '../../styles/variables'
import { Text } from '../topography'
import LanguageLocationSelector from '../selector/LanguageLocationSelector'
import { getLocationInfo } from '../../utils/locations'
import { generateKey, generateRegExp } from '../../utils/functions'
import {
  SelectedLocation,
  SelectionsWrapper,
} from '../../containers/AddProjectModal/steps/styled'
import {
  getUserTier,
  isPaidUser,
  isProUser,
} from '../../utils/freeVersionBlock'
import { getUserFreeTrialDays, isSubUser } from '../../utils/userAuth'
import {
  CloseButton,
  LinkButton,
  OutlineButton,
  PrimaryButton,
} from '../buttons/Buttons'
import { selectCachedLocations } from '../../state/selectors/locationManager'

import Modal, { ModalHeader, ModalContent, ModalFooter } from './Modal'

const desktopStyle = {
  width: 'fit-content',
  height: 'fit-content',
  minWidth: '510px',
  maxHeight: '90vh',
  overflow: 'unset',
  overflowY: 'auto',
}

const mobileStyle = {
  width: '100%',
  height: '100vh',
  borderRadius: 0,
}

const KModalFooter = styled(ModalFooter)({
  display: 'flex',
  justifyContent: 'center',
  [mediaQueries.mobile]: {
    flexDirection: 'column-reverse',
    '& button + button': {
      marginLeft: 0,
      marginTop: 0,
      marginBottom: '15px',
    },
  },
})

export default ({
  title,
  step,
  locations,
  onSubmit,
  onCancel,
  isOpen,
  isLoading,
  showUpgradeModal,
  ...rest
}) => {
  const cachedLocations = useSelector(selectCachedLocations())
  const [currentLocId, setCurrentLocId] = useState(null)
  const [currentLang, setCurrentLang] = useState('en')
  const [currentLocations, setCurrentLocations] = useState(locations || [])

  useEffect(() => {
    isOpen && setCurrentLocations(locations)
  }, [isOpen, locations])

  const disableAddLocButton =
    !currentLang ||
    !currentLocId ||
    currentLocations.some(
      (loc) =>
        generateKey([loc.languageCode, loc.id]) ===
        generateKey([currentLang, currentLocId]),
    )
  const ctaText = isPaidUser()
    ? translate('upgrade_your_plan')
    : translate('start_free_trial_button', getUserFreeTrialDays())
  const locationCount = isProUser() ? 20 : 2
  const upgradeText = isProUser()
    ? translate('tier1_upgrade_to_add_locations', ctaText, locationCount)
    : translate('free_user_upgrade_to_add_locations', ctaText, locationCount)
  const listRegExp = generateRegExp([ctaText, `<b>${locationCount}</b>`])
  const userTier = getUserTier()
  const isSubUserAccount = isSubUser()

  return (
    <Modal
      isOpen={isOpen}
      desktopStyle={desktopStyle}
      mobileStyle={mobileStyle}
      {...rest}
    >
      <ModalHeader>
        {title}
        <CloseButton onClick={onCancel} style={{ top: 20 }} />
      </ModalHeader>
      <ModalContent styles={{ padding: '35px', minHeight: '290px' }}>
        <div
          style={{
            display: 'flex',
            width: '100%',
            position: 'relative',
            alignItems: 'flex-end',
          }}
        >
          <LanguageLocationSelector
            autoSelect={false}
            allowCities={step === 3} // allowCities for Keywords Location only
            useCompetitorLocations={step === 4}
            theme={{
              color: palette.darkGray,
              bgColor: palette.lightGray10,
              borderColor: palette.lightGray,
              height: 40,
            }}
            lang={currentLang}
            locId={currentLocId}
            style={{
              display: 'flex',
              width: '100%',
            }}
            onChange={(locId, lang) => {
              setCurrentLocId(locId)
              setCurrentLang(lang)
            }}
            getSupportedLocationTooltip={(languages, optionValue) => {
              if (!languages?.length) return `<b>${optionValue}</b>`
              return `<b>${optionValue}:</b></br>${translate(
                'location_other_languages_message',
                languages
                  .map((l) => `"<b>${translate(l.code)}</b>"`)
                  .join(', '),
              )}`
            }}
            getUnsupportedLocationTooltip={(languages, optionValue) => {
              if (!languages?.length) return `<b>${optionValue}</b>`
              return `<b>${optionValue}:</b></br>${translate(
                'location_disabled_message',
                languages
                  .map((l) => `"<b>${translate(l.code)}</b>"`)
                  .join(', '),
              )}`
            }}
          />
          <OutlineButton
            static
            data-testid="add-project-location-add-button"
            color={palette.orange}
            onClick={() => {
              const newLocations = [
                ...currentLocations,
                {
                  ...getLocationInfo(currentLocId, cachedLocations),
                  languageCode: currentLang,
                },
              ]
              setCurrentLocations(newLocations)
              setCurrentLang(null)
              setCurrentLocId(null)
            }}
            style={{
              marginLeft: '8px',
              height: '40px',
              whiteSpace: 'pre',
            }}
            disabled={disableAddLocButton}
          >
            {translate('add_payment')}
          </OutlineButton>
        </div>
        {currentLocations.length === 0 && (
          <Text
            color={palette.danger}
            margin="16px 0"
            fontSize="12px"
            textAlign="center"
          >
            {translate('no_location_error_message')}
          </Text>
        )}
        <SelectionsWrapper style={{ maxHeight: '240px' }}>
          {currentLocations.map((selection) => (
            <SelectedLocation
              data-testid="selected-language-locations"
              key={generateKey([selection.languageCode, selection.id])}
            >
              <ReactCountryFlag
                countryCode={selection.countryIsoCode}
                svg
                style={{
                  marginTop: '-3px',
                  width: '1.3333em',
                  height: '1em',
                }}
              />
              <span
                style={{
                  padding: '0 20px',
                  fontSize: '14px',
                }}
              >
                {selection.type === 'Country'
                  ? `${translate(selection.languageCode)} / ${translate(
                      selection.name,
                    )}`
                  : `${translate(selection.languageCode)} / ${translate(
                      selection.canonicalName,
                    )}`}
              </span>
              <CloseButton
                data-testid="selected-location-close"
                color={palette.gray}
                style={{
                  top: '11px',
                  right: '8px',
                }}
                onClick={() => {
                  const newLocations = currentLocations.filter(
                    (l) =>
                      generateKey([selection.languageCode, selection.id]) !==
                      generateKey([l.languageCode, l.id]),
                  )
                  setCurrentLocations(newLocations)
                }}
              />
            </SelectedLocation>
          ))}
        </SelectionsWrapper>
        {userTier !== 'tier2' && userTier !== 'tier3' && (
          <Text
            lineHeight="22px"
            boldColor={palette.darkGray}
            textAlign="center"
            margin="16px 0 0 0"
          >
            {reactStringReplace(upgradeText, listRegExp, (match, index) => {
              if (match.startsWith('<b>')) {
                return <b key={index}>{locationCount}</b>
              }
              return (
                <LinkButton
                  key={index}
                  onClick={() => {
                    onCancel()
                    showUpgradeModal(null)
                  }}
                  disabled={isSubUserAccount}
                  style={{
                    textDecoration: 'underline',
                    fontSize: '14px',
                    margin: '0px',
                    display: 'inline-block',
                    fontFamily: fonts.primaryBook,
                    paddingLeft: 0,
                    paddingRight: 0,
                  }}
                >
                  {ctaText}
                </LinkButton>
              )
            })}
          </Text>
        )}
      </ModalContent>
      <KModalFooter>
        <OutlineButton static color={palette.darkGray50} onClick={onCancel}>
          {translate('cancel')}
        </OutlineButton>
        <PrimaryButton
          tag={translate(`confirm_button`)}
          isLoading={isLoading}
          disabled={currentLocations.length === 0}
          theme="orange"
          mobileWidth="100%"
          onClick={() => {
            onSubmit(
              currentLocations.map((selection) => ({
                loc_id: selection.id,
                lang: selection.languageCode,
                ...selection,
              })),
            )
          }}
          style={isLoading ? { padding: 5 } : {}}
        />
      </KModalFooter>
    </Modal>
  )
}
